import { StoreState } from '../pdf-editor.store';

type SetFn = (
  fn: (draft: StoreState) => void,
  replace?: boolean,
  actionName?: string,
) => void;

type GetFn = () => StoreState;

type ActionFn = (draft: StoreState) => void;
type NamedSetFn = (fn: ActionFn) => void;

/**
 * Creates a named set function that automatically prefixes action names for DevTools.
 * Use this for simple cases or when you need manual control over the action name.
 *
 * @param set - The zustand set function
 * @param sliceName - The name of the slice (e.g., 'pdfDocument', 'placeholders')
 * @returns A function that wraps set with automatic action naming
 *
 * @example
 * const namedSet = createNamedSet(set, 'pdfDocument');
 * namedSet('setScale', (draft) => { draft.pdfDocument.scale = 1.5; });
 * // Action appears as "pdfEditor:pdfDocument/setScale" in DevTools
 */
export const createNamedSet = (
  set: SetFn,
  sliceName: string,
): ((actionName: string, fn: ActionFn) => void) => {
  return (actionName, fn) => {
    set(fn, undefined, `pdfEditor:${sliceName}/${actionName}`);
  };
};

/**
 * Creates a set of named actions where the action name is automatically
 * inferred from the object key. No need to specify the action name manually.
 *
 * @param set - The zustand set function
 * @param sliceName - The name of the slice (e.g., 'pdfDocument', 'placeholders')
 * @param actionsFactory - A function that receives namedSet and returns an actions object
 * @returns The actions object with automatic naming
 *
 * @example
 * actions: createSliceActions(set, 'pdfDocument', (namedSet) => ({
 *   setScale: (scale: number) => namedSet((draft) => {
 *     draft.pdfDocument.scale = scale;
 *   }),
 *   resetScale: () => namedSet((draft) => {
 *     draft.pdfDocument.scale = 1;
 *   }),
 * }))
 * // Actions appear as "pdfEditor:pdfDocument/setScale", "pdfEditor:pdfDocument/resetScale"
 */
export const createSliceActions = <
  T extends Record<string, (...args: any[]) => any>,
>(
  set: SetFn,
  sliceName: string,
  actionsFactory: (namedSet: NamedSetFn) => T,
): T => {
  const result = {} as T;

  // Track current action name for the namedSet calls
  let currentActionName = '';
  const namedSet: NamedSetFn = (fn) => {
    set(fn, undefined, `pdfEditor:${sliceName}/${currentActionName}`);
  };

  // Get the actions object
  const actions = actionsFactory(namedSet);

  // Wrap each action to set the current name before execution
  for (const [name, action] of Object.entries(actions)) {
    result[name as keyof T] = ((...args: any[]) => {
      currentActionName = name;
      return (action as Function)(...args);
    }) as T[keyof T];
  }

  return result;
};

/**
 * Creates a set of named actions with access to both set and get functions.
 * Use this for slices that need to read state before making changes.
 *
 * @param set - The zustand set function
 * @param get - The zustand get function
 * @param sliceName - The name of the slice
 * @param actionsFactory - A function that receives namedSet and get, returns actions object
 * @returns The actions object with automatic naming
 *
 * @example
 * actions: createSliceActionsWithGet(set, get, 'placeholders', (namedSet, get) => ({
 *   updateField: (field) => {
 *     const currentFiles = get().placeholders.files;
 *     namedSet((draft) => { ... });
 *   },
 * }))
 */
export const createSliceActionsWithGet = <
  T extends Record<string, (...args: any[]) => any>,
>(
  set: SetFn,
  get: GetFn,
  sliceName: string,
  actionsFactory: (namedSet: NamedSetFn, get: GetFn) => T,
): T => {
  const result = {} as T;

  // Track current action name for the namedSet calls
  let currentActionName = '';
  const namedSet: NamedSetFn = (fn) => {
    set(fn, undefined, `pdfEditor:${sliceName}/${currentActionName}`);
  };

  // Get the actions object
  const actions = actionsFactory(namedSet, get);

  // Wrap each action to set the current name before execution
  for (const [name, action] of Object.entries(actions)) {
    result[name as keyof T] = ((...args: any[]) => {
      currentActionName = name;
      return (action as Function)(...args);
    }) as T[keyof T];
  }

  return result;
};
